import { Stack, Typography } from "@mui/material";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { queryMerchantInfoAndGsi, queryMerchantPreference } from "../model";
import NoticeBox from "components/NoticeBox/NoticeBox";
import PaymentGateways from "./PaymentGateways";
import { spinning } from "../utils";
import s from "./MerchantProcessorMDR.module.scss";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PortalContext } from "App";
import useActionSnackBar from "utils/useActionSnackBar/useActionSnackBar";

const processorMdrCache = {};
const MerchantProcessorMDR = ({
  fetchMerchantDetail,
  mccOptions,
  merchantId,
  merchant,
  path,
}) => {
  const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.processorMdr.";
  const { t } = useTranslation();
  const { actionSnackBar } = useActionSnackBar(PAGE_LOCALE_KEY);
  const stateContext = useContext(PortalContext);
  const { detailPageState, setDetailPageState } = stateContext;
  const [loading, setLoading] = useState(false);
  const [merchantMdrList, setMerchantMdrList] = useState([]);
  const [specialProcessor, setSpecialProcessor] = useState(undefined);
  const [lastUpdated, setLastUpdated] = useState(null);
  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const fetchMerchantInfoAndGsi = async (action) => {
    try {
      // Check cache first - only use cache if no action is specified AND we have cache
      // When action is provided (refresh/update), always fetch fresh data
      if (processorMdrCache[merchantId] && !action) {
        const cached = processorMdrCache[merchantId];
        setMerchantMdrList(cached?.data);
        setSpecialProcessor(cached?.specialProcessor);
        setLastUpdated(cached?.lastUpdated ?? null);
        setLoading(false);
        return;
      }

      // Fetch both MDR/GSI and preferences (when action is provided or no cache)
      const [merchantMdrAndGsiResp, preferenceResp] = await Promise.all([
        queryMerchantInfoAndGsi(merchantId),
        queryMerchantPreference({
          merchantId,
        }),
      ]);

      // Process MDR/GSI data
      const { data } = merchantMdrAndGsiResp;
      if (data?.responseCode === "0000" && data?.data) {
        const mdrList = data?.data;
        mdrList.forEach((merchantMdr) => {
          ["mdr", "foreignMdr", "referrerCommission"].forEach((field) => {
            merchantMdr[field] = (merchantMdr[field] * 100).toFixed(2);
          });
        });
        setMerchantMdrList(mdrList);
        processorMdrCache[merchantId] = {
          ...processorMdrCache[merchantId],
          data: mdrList,
        };
        const now = Date.now();
        setLastUpdated(now);
        processorMdrCache[merchantId].lastUpdated = now;
      }

      // Process preference data for special processor
      if (
        preferenceResp?.data?.responseCode === "0000" &&
        preferenceResp?.data?.data
      ) {
        const preference = JSON.parse(
          preferenceResp?.data?.data?.preference ?? "{}",
        );
        const specialProcessor = findSpecicalProcessor(preference);
        setSpecialProcessor(specialProcessor);
        processorMdrCache[merchantId] = {
          ...processorMdrCache[merchantId],
          specialProcessor: specialProcessor,
        };
      }
    } catch (error) {
      actionSnackBar("fetch", "failed", {errorMessage: error?.message ?? "unknown error"});
      delete processorMdrCache[merchantId];
    } finally {
      setLoading(false);
    }
  };

  const findSpecicalProcessor = (preference) => {
    const gatewayMap = preference?.gatewayMap;
    const isGatewaySwitchingForbiddenedMerchant = Object.values(
      gatewayMap,
    ).find((_paymentGateway) => {
      return (
        _paymentGateway.includes("DBS") ||
        _paymentGateway.includes("DSB") ||
        _paymentGateway.includes("GLOBALPAYMENTS")
      );
    });
    return isGatewaySwitchingForbiddenedMerchant;
  };

  useEffect(() => {
    if (merchantId) {
      // Check cache first - if exists, show immediately without loading
      if (detailPageState[path]?.processorAccountsRefreshTrigger) {
        handleRefresh();
        setDetailPageState((prev) => ({
          ...prev,
          [path]: { ...prev[path], processorAccountsRefreshTrigger: null },
        }));
      } else if (processorMdrCache[merchantId]) {
        const cached = processorMdrCache[merchantId];
        setMerchantMdrList(cached?.data);
        setSpecialProcessor(cached?.specialProcessor);
        setLastUpdated(cached?.lastUpdated ?? null);
        setLoading(false);
      } else {
        // Only fetch if no cache
        handleRefresh();
      }
    }
  }, [merchantId, detailPageState[path]?.processorAccountsRefreshTrigger]);


  const handleRefresh = async () => {
    setLoading(true);
    delete processorMdrCache[merchantId];
    await fetchMerchantInfoAndGsi("refresh");
  };

  return (
    <Stack direction="column" className={s.contentContainer}>
      <Stack 
        direction="row" 
        alignItems="center" 
        justifyContent="space-between" 
        flexWrap="wrap"
        sx={{ width: "100%", mb: 1, gap: 1 }}
      >
        <div style={{ width: "fit-content", marginBottom: "16px" }}>
          <NoticeBox>
            <Typography variant="content">
              {t(`${PAGE_LOCALE_KEY}info`)}
            </Typography>
          </NoticeBox>
        </div>
        <Stack 
          direction="column" 
          alignItems="flex-end" 
          justifyContent="center"
          spacing={0.5}
          sx={{
            display: "flex",
            alignSelf: { xs: "flex-start", sm: "stretch" },
            flexShrink: 0,
          }}
        >
          <ButtonMAS
            variant="bordered"
            size="table-bar-button"
            isLoading={loading}
            disabled={loading}
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            {t(`${PAGE_LOCALE_KEY}refresh`) || "Refresh"}
          </ButtonMAS>
          <Typography variant="caption" noWrap sx={{ color: "#666", whiteSpace: "nowrap", fontSize: "10px" }}>
            {lastUpdated
              ? `${t(`${PAGE_LOCALE_KEY}lastUpdated`, { defaultValue: "Last updated" })}: ${formatTime(lastUpdated)}`
              : t(`${PAGE_LOCALE_KEY}neverUpdated`, { defaultValue: "-" })}
          </Typography>
        </Stack>
      </Stack>
      {!loading && specialProcessor && `Processor: ${specialProcessor}`}
      {loading ? (
        spinning()
      ) : (
        <PaymentGateways
          merchantMdrList={merchantMdrList}
          mccOptions={mccOptions}
          fetchMerchantInfoAndGsi={fetchMerchantInfoAndGsi}
          merchantId={merchantId}
          fetchMerchantDetail={fetchMerchantDetail}
          merchant={merchant}
          path={path}
        />
      )}
    </Stack>
  );
};

export default React.memo(MerchantProcessorMDR);
